import {
  Component,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  NgZone
} from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

interface BankNode {
  mesh: THREE.Group;
  disc: THREE.Mesh;
  borderRing: THREE.Mesh;
  basePos: THREE.Vector3;
  index: number;
  appearBeyond: number;
  discMats: THREE.Material[];
}

interface Pulse {
  mesh: THREE.Mesh;
  from: THREE.Vector3;
  to: THREE.Vector3;
  progress: number;
  speed: number;
  nodeIndex: number;
}

@Component({
  selector: 'app-neural-bank',
  templateUrl: './neural-bank.component.html',
  styleUrls: ['./neural-bank.component.scss']
})
export class NeuralBankComponent implements AfterViewInit, OnDestroy {

  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private controls!: OrbitControls;
  private animationId!: number;
  private clock = new THREE.Clock();
  private bankNodes: BankNode[] = [];
  private connectionLines: { line: THREE.Line; offset: number; appearBeyond: number }[] = [];
  private pulses: Pulse[] = [];
  private centerGroup!: THREE.Group;
  private lastBeat = -99;
  private readonly beatInterval = 4.5;
  private visibilityObserver!: IntersectionObserver;
  private isVisible = false;

  // Tier 0: outer ring, always visible
  // Tier 1: middle ring, appear when camera distance < 40
  // Tier 2: inner ring, appear when camera distance < 26
  private bankTiers: { logos: string[]; radius: number; appearBeyond: number }[] = [
    {
      logos: [
        'assets/img/sistemas/notalize.jpg',
        'assets/img/bradesco.svg',
        'assets/img/caixa.svg',
        'assets/img/itau.svg',
        'assets/img/sistemas/sieg.jpg',
        'assets/img/santander.svg',
        'assets/img/sicredi.svg',
        'assets/img/sicoob.svg',
        'assets/img/sistemas/jettax.jpg',
        'assets/img/btg.svg',
        'assets/img/banrisul.svg',
        'assets/img/bancodobrasil.svg',
        'assets/img/nubank.svg',
        'assets/img/mercadopago.svg',
        'assets/img/bmg.svg',
        'assets/img/unicred.svg',
        'assets/img/safra.svg',
        'assets/img/xp.svg',
        'assets/img/bv.svg',
        'assets/img/inter.svg',
        'assets/img/cora.svg',
        'assets/img/infinitepay.svg',
        'assets/img/sistemas/contabit.png',


      ],
      radius: 17,
      appearBeyond: Infinity,
    },
    {
      logos: [
        'assets/img/c6bank.svg',
        'assets/img/neon.svg',
        'assets/img/stone.svg',
        'assets/img/picpay.svg',
        'assets/img/pagbank.svg',
        'assets/img/cresol.svg',
        'assets/img/credisis.svg',
        'assets/img/asaas.svg',
        'assets/img/omie.svg',
        'assets/img/magalupay.svg',
        'assets/img/recargapay.svg',
        'assets/img/contasimples.svg',
        'assets/img/efi.svg',
        'assets/img/original.svg',
        'assets/img/votorantim.svg',
      ],
      radius: 27,
      appearBeyond: 52,
    },
    {
      logos: [
        'assets/img/ailos.svg',
        'assets/img/duepay.svg',
        'assets/img/grafeno.svg',
        'assets/img/iugo.svg',
        'assets/img/bankofamerica.svg',
        'assets/img/bnb2.svg',
        'assets/img/uniprime.svg',
        'assets/img/sulcredi.svg',
        'assets/img/linker.svg',
        'assets/img/beesbank.svg',
      ],
      radius: 38,
      appearBeyond: 65,
    },
  ];

  constructor(private ngZone: NgZone) {}

  private initialized = false;

  ngAfterViewInit(): void {
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const intersecting = entries[0].isIntersecting;
        if (intersecting && !this.initialized) {
          this.initialized = true;
          this.ngZone.runOutsideAngular(() => this.init());
        }
        this.isVisible = intersecting;
        if (this.isVisible && this.initialized) this.animate();
      },
      { threshold: 0.05 }
    );
    this.visibilityObserver.observe(this.canvasRef.nativeElement);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationId);
    this.controls?.dispose();
    this.renderer?.dispose();
    this.visibilityObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      cancelAnimationFrame(this.animationId);
    } else if (this.isVisible && this.initialized) {
      this.animate();
    }
  }

  private init(): void {
    const canvas = this.canvasRef.nativeElement;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    this.scene = new THREE.Scene();
    this.addStars();

    this.camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 500);
    this.camera.position.set(0, 0, 42);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
    this.renderer.setClearColor(0x000000, 0);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.rotateSpeed = 0.7;
    this.controls.zoomSpeed = 0.8;
    this.controls.minDistance = 15;
    this.controls.maxDistance = 110;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.5;
    this.controls.enablePan = false;
    this.controls.enableZoom = false;

    canvas.addEventListener('mouseenter', () => this.controls.enableZoom = true);
    canvas.addEventListener('mouseleave', () => this.controls.enableZoom = false);

    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambient);
    const pointLight = new THREE.PointLight(0x7c3aed, 2, 60);
    pointLight.position.set(0, 0, 0);
    this.scene.add(pointLight);
    const pointLight2 = new THREE.PointLight(0xdb2777, 1.5, 60);
    pointLight2.position.set(10, 10, 5);
    this.scene.add(pointLight2);

    this.buildNetwork();

    window.addEventListener('resize', this.onResize);
  }

  private addStars(): void {
    const count = 400;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 300;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 300;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 300;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.15, transparent: true, opacity: 0.5 });
    this.scene.add(new THREE.Points(geo, mat));
  }

  private encodePath(path: string): string {
    return path.split('/').map(seg => encodeURIComponent(seg)).join('/');
  }

  private buildNetwork(): void {
    const loader = new THREE.TextureLoader();
    const goldenRatio = (1 + Math.sqrt(5)) / 2;

    // ─── Centro: Logo Mister como medalha 3D ────────────
    this.centerGroup = new THREE.Group();

    // Disco principal (moeda 3D)
    const discGeo = new THREE.CylinderGeometry(4.2, 4.2, 0.5, 48);
    const discMats = [
      new THREE.MeshStandardMaterial({ color: 0x2d1b69, roughness: 0.3, metalness: 0.8 }), // lateral
      new THREE.MeshStandardMaterial({ color: 0x0d0020, roughness: 0.2, metalness: 0.9, emissive: new THREE.Color(0x3b0764), emissiveIntensity: 0.5 }), // topo (frente)
      new THREE.MeshStandardMaterial({ color: 0x1a0050, roughness: 0.3, metalness: 0.8, emissive: new THREE.Color(0x4c1d95), emissiveIntensity: 0.6 }), // base (trás)
    ];
    const centerDisc = new THREE.Mesh(discGeo, discMats);
    centerDisc.rotation.x = Math.PI / 2;
    this.centerGroup.add(centerDisc);

    // Anel de borda brilhante
    const borderRing = new THREE.Mesh(
      new THREE.TorusGeometry(4.2, 0.12, 8, 48),
      new THREE.MeshStandardMaterial({ color: 0x7c3aed, emissive: new THREE.Color(0x7c3aed), emissiveIntensity: 1.2, roughness: 0.1, metalness: 1 })
    );
    this.centerGroup.add(borderRing);

    // Anel orbitando inclinado
    const orbitRing = new THREE.Mesh(
      new THREE.TorusGeometry(5.8, 0.07, 8, 48),
      new THREE.MeshStandardMaterial({ color: 0xdb2777, emissive: new THREE.Color(0xdb2777), emissiveIntensity: 0.9, roughness: 0.1, metalness: 1 })
    );
    orbitRing.rotation.set(Math.PI / 3, Math.PI / 5, 0);
    this.centerGroup.add(orbitRing);

    // Segundo anel orbitando
    const orbitRing2 = new THREE.Mesh(
      new THREE.TorusGeometry(5.8, 0.07, 8, 48),
      new THREE.MeshStandardMaterial({ color: 0x7c3aed, emissive: new THREE.Color(0x7c3aed), emissiveIntensity: 0.9, roughness: 0.1, metalness: 1 })
    );
    orbitRing2.rotation.set(-Math.PI / 4, Math.PI / 3, 0);
    this.centerGroup.add(orbitRing2);

    // Glow central
    const centerGlow = new THREE.PointLight(0x7c3aed, 3, 20);
    this.centerGroup.add(centerGlow);

    // Logo sobre a face frontal do disco
    loader.load('assets/img/logo.png', (tex) => {
      const logoMat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const logoMesh = new THREE.Mesh(new THREE.PlaneGeometry(6.5, 3.4), logoMat);
      logoMesh.position.z = 0.4;
      this.centerGroup.add(logoMesh);
    });

    this.scene.add(this.centerGroup);

    // ─── Nós por tier ──────────────────────────────────
    this.bankTiers.forEach((tier) => {
      const total = tier.logos.length;
      const initialOpacity = tier.appearBeyond === Infinity ? 1 : 0;

      tier.logos.forEach((logoPath, i) => {
        const phi = Math.acos(1 - 2 * (i + 0.5) / total);
        const theta = 2 * Math.PI * i / goldenRatio;
        const r = tier.radius + (Math.random() - 0.5) * 2;
        const pos = new THREE.Vector3(
          r * Math.sin(phi) * Math.cos(theta),
          r * Math.sin(phi) * Math.sin(theta),
          r * Math.cos(phi)
        );

        const group = new THREE.Group();
        group.position.copy(pos);

        // Disco circular
        const discGeo = new THREE.CylinderGeometry(1.7, 1.7, 0.25, 24);
        const discMats: THREE.Material[] = [
          new THREE.MeshBasicMaterial({ color: 0x1a1a2e, transparent: true, opacity: initialOpacity }),
          new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: initialOpacity }),
          new THREE.MeshBasicMaterial({ color: 0x2d1b69, transparent: true, opacity: initialOpacity }),
        ];
        const disc = new THREE.Mesh(discGeo, discMats);
        disc.rotation.x = Math.PI / 2;
        group.add(disc);

        // Anel de borda
        const borderRingMat = new THREE.MeshBasicMaterial({
          color: 0x7c3aed,
          transparent: true,
          opacity: initialOpacity * 0.6,
        });
        const borderRing = new THREE.Mesh(new THREE.TorusGeometry(1.7, 0.06, 6, 32), borderRingMat);
        group.add(borderRing);

        // Logo (async)
        loader.load(this.encodePath(logoPath), (tex) => {
          const logoMat = new THREE.MeshBasicMaterial({
            map: tex,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide,
            opacity: initialOpacity,
          });
          const logo = new THREE.Mesh(new THREE.CircleGeometry(1.3, 24), logoMat);
          logo.position.z = 0.14;
          group.add(logo);
        });

        group.visible = initialOpacity > 0;
        this.scene.add(group);
        this.bankNodes.push({ mesh: group, disc, borderRing, basePos: pos.clone(), index: i, appearBeyond: tier.appearBeyond, discMats });

        // Linha ao centro
        this.createLine(new THREE.Vector3(0, 0, 0), pos, tier.appearBeyond);
      });
    });

    // Conexões entre vizinhos e pulsos (após todos os nós criados)
    setTimeout(() => {
      this.buildNeighborConnections();
      this.buildPulses();
    }, 50);
  }

  private buildNeighborConnections(): void {
    this.bankNodes.forEach((node, i) => {
      // Conecta aos 3 vizinhos mais próximos do mesmo tier
      const sameGroup = this.bankNodes.filter((_, j) => j !== i && this.bankNodes[j].appearBeyond === node.appearBeyond);
      const nearest = sameGroup
        .sort((a, b) => node.basePos.distanceTo(a.basePos) - node.basePos.distanceTo(b.basePos))
        .slice(0, 3);

      nearest.forEach(neighbor => {
        // Evita linha duplicada (só cria se índice menor para não duplicar)
        if (this.bankNodes.indexOf(neighbor) > i) {
          this.createLine(node.basePos, neighbor.basePos, node.appearBeyond);
        }
      });
    });
  }

  private buildPulses(): void {
    const pulseGeo = new THREE.SphereGeometry(0.18, 8, 8);
    const center = new THREE.Vector3(0, 0, 0);
    const tier0 = this.bankNodes.filter(n => n.appearBeyond === Infinity);

    // 1 pulso por nó tier-0, todos parados em progress=1 esperando o próximo batimento
    tier0.forEach((node, i) => {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(pulseGeo, mat);
      mesh.position.copy(node.basePos); // começa no destino (parado)
      this.scene.add(mesh);
      this.pulses.push({
        mesh,
        from: center.clone(),
        to: node.basePos.clone(),
        progress: 1, // parado até o próximo batimento
        speed: 0.006 + Math.random() * 0.002, // velocidade consistente entre nós
        nodeIndex: i,
      });
    });
  }

  private triggerBeat(): void {
    // Reinicia todos os pulsos simultaneamente do centro
    this.pulses.forEach(pulse => {
      pulse.progress = 0;
    });
  }

  private createLine(from: THREE.Vector3, to: THREE.Vector3, appearBeyond: number): void {
    const initialOpacity = appearBeyond === Infinity ? 0.5 : 0;
    const geo = new THREE.BufferGeometry().setFromPoints([from.clone(), to.clone()]);
    const mat = new THREE.LineBasicMaterial({
      color: 0x7c3aed,
      transparent: true,
      opacity: initialOpacity,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const line = new THREE.Line(geo, mat);
    line.visible = appearBeyond === Infinity;
    this.scene.add(line);
    this.connectionLines.push({ line, offset: Math.random() * Math.PI * 2, appearBeyond });
  }

  private animate = (): void => {
    if (!this.isVisible) return;
    this.animationId = requestAnimationFrame(this.animate);
    const t = this.clock.getElapsedTime();
    const camDist = this.camera.position.length();

    // Centro: medalha olha para a câmera + anéis orbitam
    if (this.centerGroup) {
      this.centerGroup.lookAt(this.camera.position);
      // anel de borda: sem animação extra (já segue o grupo)
      const orbit1 = this.centerGroup.children[2] as THREE.Mesh;
      const orbit2 = this.centerGroup.children[3] as THREE.Mesh;
      if (orbit1) orbit1.rotation.z = t * 0.5;
      if (orbit2) orbit2.rotation.z = -t * 0.4;
    }

    // Cards por tier
    this.bankNodes.forEach((node) => {
      const tierOpacity = node.appearBeyond === Infinity
        ? 1
        : Math.max(0, Math.min(1, (camDist - node.appearBeyond) / 8));

      node.mesh.visible = tierOpacity > 0.01;
      if (!node.mesh.visible) return;

      // Billboard + float
      node.mesh.lookAt(this.camera.position);
      node.mesh.position.set(
        node.basePos.x + Math.cos(t * 0.6 + node.index * 0.3) * 0.2,
        node.basePos.y + Math.sin(t * 0.8 + node.index * 0.5) * 0.3,
        node.basePos.z
      );

      // Disco (mats em cache)
      for (let k = 0; k < node.discMats.length; k++) {
        (node.discMats[k] as THREE.MeshBasicMaterial).opacity = tierOpacity;
      }

      // Borda pulsa
      const pulseOpacity = (0.4 + Math.sin(t * 1.5 + node.index * 0.4) * 0.3) * tierOpacity;
      (node.borderRing.material as THREE.MeshBasicMaterial).opacity = pulseOpacity;

      // Logo (pode não ter carregado ainda)
      const children = node.mesh.children;
      for (let k = 0; k < children.length; k++) {
        const child = children[k];
        if (child !== node.disc && child !== node.borderRing && (child as THREE.Mesh).material) {
          ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = tierOpacity;
        }
      }
    });

    // Pulso + fade das conexões
    this.connectionLines.forEach(({ line, offset, appearBeyond }) => {
      const tierOpacity = appearBeyond === Infinity
        ? 1
        : Math.max(0, Math.min(1, (camDist - appearBeyond) / 8));

      line.visible = tierOpacity > 0.01;
      if (!line.visible) return;
      (line.material as THREE.LineBasicMaterial).opacity =
        (0.15 + Math.sin(t * 1.8 + offset) * 0.15) * tierOpacity * 1.8;
    });

    // ─── Batimento cardíaco ───────────────────────────
    const timeSinceBeat = t - this.lastBeat;
    if (timeSinceBeat >= this.beatInterval) {
      this.lastBeat = t;
      this.triggerBeat();
    }

    // Animação do centro: respiração contínua suave + spike rápido no batimento
    if (this.centerGroup) {
      // Respiração base: sobe e desce gentilmente no ritmo do batimento
      const breathe = 0.04 * Math.sin((t / this.beatInterval) * Math.PI * 2);
      // Spike rápido no momento do batimento (dura ~0.45s independente do intervalo)
      const spikeDecay = Math.max(0, 1 - timeSinceBeat / 0.45);
      const spike = spikeDecay * spikeDecay * 0.10; // curva quadrática = mais suave
      const centerScale = 1 + breathe + spike;

      const disc = this.centerGroup.children[0] as THREE.Mesh;
      if (disc) disc.scale.setScalar(centerScale);

      const glow = this.centerGroup.children[4] as THREE.PointLight;
      if (glow?.isLight) glow.intensity = 3 + spikeDecay * 5 + breathe * 30;
    }

    // ─── Pulsos neurais: centro → nós ────────────────
    // this.pulses.forEach((pulse) => {
    //   if (pulse.progress >= 1) {
    //     pulse.mesh.visible = false;
    //     return;
    //   }

    //   pulse.progress = Math.min(pulse.progress + pulse.speed, 1);
    //   pulse.mesh.visible = true;
    //   pulse.mesh.position.lerpVectors(pulse.from, pulse.to, pulse.progress);

    //   // Fade: aparece rápido, some suavemente ao chegar
    //   const fade = pulse.progress < 0.1
    //     ? pulse.progress / 0.1
    //     : pulse.progress > 0.75
    //       ? 1 - (pulse.progress - 0.75) / 0.25
    //       : 1;

    //   pulse.mesh.scale.setScalar(1.2 * fade);
    //   (pulse.mesh.material as THREE.MeshBasicMaterial).opacity = 0.95 * fade;
    // });

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  private onResize = (): void => {
    const canvas = this.canvasRef.nativeElement;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }
}
